import {
  DEFAULT_DAILY_RULE,
  isImportOutcome,
  isSelectableOutcome,
  ownerScopedSourceKey,
  stableImportedId,
  type Category,
  type Entry,
  type MatrixDocument,
  type Operation,
} from '../../packages/domain/src/index'

export interface ImportPreview {
  readonly fingerprint: string
  readonly rollbackFingerprint: string
  readonly additions: readonly string[]
  readonly replacements: readonly string[]
  readonly rejections: readonly string[]
  readonly rollbackJson: string
  readonly nextDocument: MatrixDocument
}

export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`
  if (value && typeof value === 'object') {
    return `{${Object.entries(value as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right)).map(([key, child]) => `${JSON.stringify(key)}:${canonicalJson(child)}`).join(',')}}`
  }
  if (typeof value === 'number' && !Number.isFinite(value)) throw new Error('Non-canonical import number')
  if (value === undefined || typeof value === 'function' || typeof value === 'symbol') throw new Error('Non-canonical import value')
  return JSON.stringify(value)
}

async function sha256(value: string): Promise<string> {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))
  return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

function entryKey(entry: Entry): string { return `${entry.operationId}\u0000${entry.date}` }

function categorySourceKey(ownerId: string, category: Category): string {
  return category.sourceKey ?? ownerScopedSourceKey({ ownerId, entity: 'category', sourceId: category.id })
}

function operationSourceKey(ownerId: string, operation: Operation): string {
  return operation.sourceKey ?? ownerScopedSourceKey({ ownerId, entity: 'operation', sourceId: operation.id })
}

function assertOwnerSourceKey(ownerId: string, entity: 'category' | 'operation', sourceKey: string): void {
  const prefix = `${encodeURIComponent(ownerId)}:${entity}:`
  if (!sourceKey.startsWith(prefix)) throw new Error(`Cross-owner ${entity} source key rejected`)
}

export async function stageOwnerImport(current: MatrixDocument, incoming: MatrixDocument): Promise<ImportPreview> {
  if (incoming.ownerId !== current.ownerId) throw new Error('Cross-owner import rejected')
  const additions: string[] = []
  const replacements: string[] = []
  const rejections: string[] = []
  const categories = new Map(current.categories.map((category) => [category.id, category]))
  const categoryBySource = new Map(current.categories.map((category) => [categorySourceKey(current.ownerId, category), category]))
  const operations = new Map(current.operations.map((operation) => [operation.id, operation]))
  const operationBySource = new Map(current.operations.map((operation) => [operationSourceKey(current.ownerId, operation), operation]))
  const entries = new Map(current.entries.map((entry) => [entryKey(entry), entry]))
  const importedCategoryId = new Map<string, string>()
  const importedOperationId = new Map<string, string>()

  for (const category of incoming.categories) {
    const sourceKey = categorySourceKey(current.ownerId, category)
    assertOwnerSourceKey(current.ownerId, 'category', sourceKey)
    const existing = categoryBySource.get(sourceKey)
    if (existing) {
      replacements.push(`Category ${sourceKey} (identity matched; history preserved)`)
      importedCategoryId.set(category.id, existing.id)
      continue
    }
    const id = `src:${sourceKey}`
    if (categories.has(id)) throw new Error('Category ID collision rejected')
    const next: Category = { ...category, id, sourceKey, operationOrder: [] }
    additions.push(`Category ${sourceKey}`)
    categories.set(id, next)
    categoryBySource.set(sourceKey, next)
    importedCategoryId.set(category.id, id)
  }
  for (const operation of incoming.operations) {
    const resolvedCategoryId = importedCategoryId.get(operation.categoryId) ?? operation.categoryId
    if (!categories.has(resolvedCategoryId)) {
      rejections.push(`Operation ${operation.id}: unknown category`)
      continue
    }
    const sourceKey = operationSourceKey(current.ownerId, operation)
    assertOwnerSourceKey(current.ownerId, 'operation', sourceKey)
    const existing = operationBySource.get(sourceKey)
    if (existing) {
      replacements.push(`Operation ${sourceKey} (identity matched; history preserved)`)
      importedOperationId.set(operation.id, existing.id)
      continue
    }
    const id = `src:${sourceKey}`
    if (operations.has(id)) throw new Error('Operation ID collision rejected')
    const next: Operation = {
      ...operation,
      id,
      categoryId: resolvedCategoryId,
      private: true,
      sourceKey,
      ruleVersions: [{ id: `${id}:rule:import`, effectiveFrom: operation.activePeriods[0]?.from ?? '2026-08-19', rule: DEFAULT_DAILY_RULE }],
    }
    additions.push(`Operation ${sourceKey} (private, daily)`)
    operations.set(id, next)
    operationBySource.set(sourceKey, next)
    importedOperationId.set(operation.id, id)
    const category = categories.get(resolvedCategoryId)!
    categories.set(resolvedCategoryId, { ...category, operationOrder: [...category.operationOrder, id] })
  }
  for (const entry of incoming.entries) {
    const resolvedOperationId = importedOperationId.get(entry.operationId) ?? entry.operationId
    if (!operations.has(resolvedOperationId)) {
      rejections.push(`Entry ${entry.operationId}/${entry.date}: unknown operation`)
      continue
    }
    if (!isSelectableOutcome(entry.outcome) && !isImportOutcome(entry.outcome)) {
      rejections.push(`Entry ${entry.operationId}/${entry.date}: invalid outcome`)
      continue
    }
    const nextEntry = { ...entry, operationId: resolvedOperationId }
    const key = entryKey(nextEntry)
    ;(entries.has(key) ? replacements : additions).push(`Entry ${entry.operationId}/${entry.date}`)
    entries.set(key, nextEntry)
  }

  const nextDocument: MatrixDocument = {
    ...current,
    categories: [...categories.values()],
    operations: [...operations.values()],
    entries: [...entries.values()],
  }
  const rollbackJson = canonicalJson(current)
  const rollbackFingerprint = await sha256(rollbackJson)
  const importDocument = {
    ownerId: current.ownerId,
    categories: nextDocument.categories,
    operations: nextDocument.operations,
    entries: nextDocument.entries,
  }
  const fingerprint = await sha256(canonicalJson(importDocument))
  return { fingerprint, rollbackFingerprint, additions, replacements, rejections, rollbackJson, nextDocument }
}

export function syntheticImportDocument(ownerId: string): MatrixDocument {
  const categoryId = stableImportedId({ ownerId, entity: 'category', sourceId: 'synthetic-import' })
  const operationId = stableImportedId({ ownerId, entity: 'operation', sourceId: 'synthetic-import/walk' })
  return {
    ownerId,
    categories: [{ id: categoryId, name: 'Synthetic import preview', activePeriods: [{ from: '2026-08-18', toExclusive: null }], operationOrder: [operationId] }],
    operations: [{
      id: operationId,
      categoryId,
      name: 'Synthetic imported walk',
      private: false,
      activePeriods: [{ from: '2026-08-18', toExclusive: null }],
      ruleVersions: [{ id: `${operationId}:incoming-rule`, effectiveFrom: '2026-08-18', rule: { quota: 2, weekdays: null, period: { kind: 'week' }, part: { kind: 'whole' } } }],
    }],
    entries: [
      { operationId, date: '2026-08-18', outcome: 'T' },
      { operationId, date: '2026-08-19', outcome: 'M' },
    ],
  }
}

export function downloadText(filename: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

